<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12" lg="6" sm="12">
        <v-card>
          <v-list-item>
            <v-list-item-avatar>
                <v-icon>mdi-toolbox</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
                <v-list-item-title class="headline">機材情報編集</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-card-text>
            <v-form ref="equipment_form" v-model="valid" @submit.prevent>
              <v-select label="機材種別" v-model="equipment_type" :rules="equipmentTypeRules" item-text="name" item-value="id" :items="equipment_types" no-data-text="データがありません" dense outlined return-object/>
              <v-text-field label="機材名称" v-model="name" :rules="equipmentNameRules" dense outlined></v-text-field>
              <v-select label="自社区分" v-model="in_house_class" :rules="inHouseClassRules" item-text="name" item-value="id" :items="in_house_classes" no-data-text="データがありません" dense outlined return-object/>
              <v-select label="取引先" v-model="supplier" :rules="suppliersRules" item-text="name" item-value="id" :items="suppliers" no-data-text="データがありません" dense outlined return-object/>
              <v-text-field label="単価" v-model.number="unit_price" :rules="unitPriceRules" dense outlined></v-text-field>
              <v-menu min-width="290px" max-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-on="on" label="使用予定開始日" v-model="useStartFormatted" append-outer-icon="mdi-calendar-month" readonly dense outlined
                    @blur="use_start_date = parseDate(useStartFormatted)"/>
                </template>
                <v-date-picker v-model="use_start_date" no-title scrollable actions locale="ja-jp" :close-on-content-click="true" :day-format="date => new Date(date).getDate()"></v-date-picker>
              </v-menu>
              <v-menu min-width="290px" max-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-on="on" label="使用予定終了日" v-model="useEndFormatted" append-outer-icon="mdi-calendar-month" readonly dense outlined
                    @blur="use_end_date = parseDate(useEndFormatted)"/>
                </template>
                <v-date-picker v-model="use_end_date" no-title scrollable actions locale="ja-jp" :close-on-content-click="true" :day-format="date => new Date(date).getDate()"></v-date-picker>
              </v-menu>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1" class="mr-3" :to="{ name: 'EquipmentMaster' }" depressed>キャンセル</v-btn>
            <v-btn color="success" @click.prevent="checkDialog" :disabled="!valid" depressed>確認</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row justify="center">
      <custom-dialog :maxWith="dialogMaxWith" :title="dialogTitle" :items="dialogItems" :drawer="dialogDrawer" visibleUpdate
        @updated="submit" @canceled="dialogCancel"/>
    </v-row>
  </v-container>
</template>

<script>
  import { useAppInsights } from '@/plugins/application-insights';
  import EquipmentApi from "@/api/equipment-api";
  import CustomDialog from "@/components/custom_ui/CustomDialog";
  import Rule from "@/validation/rules";

  export default {
    name: "EditEquipmentMaster",
    components: {
      CustomDialog
    },
    data: () => ({
      // フォームの項目
      loading: false,
      equipment_type: '',
      name: '',
      in_house_class: '',
      supplier: '',
      unit_price: '',
      use_start_date: '',
      useStartFormatted: "",
      use_end_date: '',
      useEndFormatted: "",

      // プルダウン項目
      equipment_types: [
        { id: 1, name: "機材" },
        { id: 2, name: "車両" },
      ],
      in_house_classes: [
        { id: 1, name: "自社" },
        { id: 2, name: "リース" },
      ],
      suppliers: '',

      // 更新ダイアログ
      dialogMaxWith: 500,
      dialogTitle: '',
      dialogItems: [],
      dialogDrawer: false,
      editId: null,

      // バリデーション関連
      valid: true,
      equipmentTypeRules: Rule.equipmentTypeRules,
      equipmentNameRules: Rule.equipmentNameRules,
      inHouseClassRules: Rule.inHouseClassRules,
      suppliersRules: Rule.suppliersRules,
      unitPriceRules: Rule.unitPriceRules,
      useDateRules: Rule.useDateRules,
    }),
    created () {
      this.initialize();
    },
    watch: {
      'use_start_date': function(){
        if (this.use_end_date < this.use_start_date) {
            this.use_end_date = this.use_start_date;
        }
        this.useStartFormatted = this.formatDate(this.use_start_date);
      },
      'use_end_date': function(){
        if (this.use_start_date > this.use_end_date) {
            this.use_start_date = this.use_end_date;
        }
        this.useEndFormatted = this.formatDate(this.use_end_date);
      },
    },
    methods: {
      isLoading() {
        this.loading = !this.loading;
        this.$emit("loading", this.loading);
      },
      initialize () {
        this.isLoading();
        this.equipmentApi = new EquipmentApi();
        // 機材IDをルートパラメータから取得
        this.editId = this.$route.params.id;
        const promise = this.equipmentApi.info(this.editId);
        promise.then(response => {
            this.equipment_type = this.equipment_types.find((v) => v.id === response.data.body.equipment_info.equipment_type);
            this.name = response.data.body.equipment_info.name;
            this.unit_price = response.data.body.equipment_info.unit_price;
            this.in_house_class = this.in_house_classes.find((v) => v.id === response.data.body.equipment_info.in_house_class);
            this.suppliers = response.data.body.suppliers_list;
            this.supplier = this.suppliers.find((v) => v.id === response.data.body.equipment_info.suppliers_id);
            this.use_start_date = this.parseDate(response.data.body.equipment_info.use_start_date);
            this.useStartFormatted = response.data.body.equipment_info.use_start_date;
            this.use_end_date = this.parseDate(response.data.body.equipment_info.use_end_date);
            this.useEndFormatted = response.data.body.equipment_info.use_end_date;
        }).catch(error => {
          useAppInsights().trackException({exception: error});
          if(error.response){
            this.$emit("error",error.response.data.message);
          }else{
            this.$emit("error","サーバーエラーが発生しました。");
          }
        }).finally(() => {
          this.isLoading();
        });
      },
      // 日付フォーマット
      formatDate (date) {
        if (!date) return null;
        const [year, month, day] = date.split('-');
        return `${year}/${month}/${day}`;
      },
      parseDate (date) {
        if (!date) return null;
        const [year,month, day] = date.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      },
      // 更新ボタン押下時イベント
      checkDialog () {
        this.dialogTitle = '更新しますか？';
        this.dialogItems = [
          { name: '機材種別', value: this.equipment_type.name },
          { name: '機材名称', value: this.name },
          { name: '自社区分', value: this.in_house_class.name },
          { name: '取引先', value: this.supplier.name },
          { name: '単価', value: this.unit_price },
          { name: '使用予定開始日', value: this.use_start_date },
          { name: '使用予定終了日', value: this.use_end_date},
        ];
        this.dialogDrawer = true;
      },
      dialogCancel() {
        this.dialogDrawer = false;
      },
      // 確認ダイアログの「更新」確定ボタン押下イベント
      submit() {
        if (this.$refs.equipment_form.validate()) {
          this.isLoading();
          const params = {
            equipment_type: this.equipment_type.id,
            name: this.name,
            in_house_class: this.in_house_class.id,
            suppliers_id: this.supplier.id,
            unit_price: this.unit_price,
            use_start_date: this.use_start_date,
            use_end_date: this.use_end_date,
          };
          const promise = this.equipmentApi.update(this.editId, params);
          promise.then((response) => {
              this.$router.push({ name: 'EquipmentMaster' });
              this.$emit('success', response.data.message);
          }).catch(error => {
            useAppInsights().trackException({exception: error});
            if(error.response){
              this.$emit("error",error.response.data.message);
            }else{
              this.$emit("error","サーバーエラーが発生しました。");
            }
          }).finally(() => {
            this.isLoading();
          });
        }
      }
    },
  };
</script>
